<template>
  <div
    class="failed-dialog-container"
    role="dialog"
    aria-modal="true"
  >
    <div class="failed-dialog-card">
      <!-- Success State -->
      <div
        v-if="refundSuccess"
        class="text-center py-4 px-2"
      >
        <div class="status-icon-wrapper success mb-4">
          <font-awesome-icon
            icon="check-circle"
            class="status-icon"
          />
        </div>
        <h3 class="dialog-title mb-3">
          {{ $t('failed_dialog.refund_requested_title') }}
        </h3>
        <p class="dialog-description mb-4">
          {{
            isAutomaticRefund
              ? $t('failed_dialog.automatic_refund_success_message')
              : $t('failed_dialog.refund_requested_message')
          }}
        </p>
        <BaseButton
          block
          size="sm"
          variant="primary"
          @click="finishRefund"
        >
          {{ $t('failed_dialog.close_button') }}
        </BaseButton>
      </div>

      <!-- Refund Confirmation State -->
      <div
        v-else-if="showRefundConfirmation"
        class="text-center py-4 px-2"
      >
        <div class="status-icon-wrapper warning mb-4">
          <font-awesome-icon
            icon="circle-question"
            class="status-icon"
          />
        </div>
        <h3 class="dialog-title mb-3">
          {{ $t('failed_dialog.refund_confirmation_title') }}
        </h3>
        <p class="dialog-description mb-4">
          {{ $t('failed_dialog.refund_confirmation_message') }}
        </p>

        <div class="d-flex flex-column align-items-center gap-3">
          <BaseButton
            block
            constrained
            size="sm"
            variant="primary"
            :disabled="loading"
            @click="performRefund(false)"
          >
            {{ $t('failed_dialog.button_confirm_refund') }}
          </BaseButton>

          <BaseButton
            block
            constrained
            size="sm"
            variant="outline-secondary"
            class="btn-cancel"
            :disabled="loading"
            @click="cancelRefund"
          >
            {{ $t('failed_dialog.button_cancel_refund') }}
          </BaseButton>
        </div>
      </div>

      <!-- Main Content -->
      <template v-else>
        <h3 class="dialog-title text-center mb-2">
          {{ $t('failed_dialog.title_battery_not_removed') }}
        </h3>

        <p class="section-subtitle text-center mb-4">
          {{ $t('failed_dialog.question_what_problem') }}
        </p>

        <!-- Error Tags Section -->
        <div class="tags-container mb-4">
          <button
            v-for="tag in tags"
            :key="tag.id"
            :class="['modern-tag', { active: tag.active }]"
            @click="onTagClick(tag.id)"
          >
            {{ tag.tag }}
          </button>
        </div>

        <hr class="separator mb-4" >

        <!-- Countdown Timer -->
        <div class="timer-card mb-4">
          <div class="timer-info">
            <font-awesome-icon
              icon="clock"
              class="timer-icon"
            />
            <div class="timer-text">
              <span class="timer-label">{{ $t('failed_dialog.automatic_refund_message') }}</span>
              <span class="timer-sublabel">{{ $t('failed_dialog.subtitle_safe_balance') }}</span>
            </div>
          </div>
          <div
            class="timer-value"
            :class="{ 'timer-low': timeLeft < 60 }"
          >
            {{ formattedTime }}
          </div>
        </div>

        <!-- Error Message -->
        <div
          v-if="errorMessage"
          class="error-alert mb-4"
          role="alert"
        >
          <font-awesome-icon
            icon="triangle-exclamation"
            class="me-2"
          />
          {{ errorMessage }}
        </div>

        <!-- Action Buttons -->
        <div class="d-flex flex-column align-items-center gap-3">
          <BaseButton
            block
            constrained
            size="sm"
            variant="primary"
            :disabled="loading"
            @click="handleTryAgain"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t('failed_dialog.button_try_again') }}
          </BaseButton>

          <BaseButton
            block
            constrained
            size="sm"
            variant="outline-secondary"
            class="btn-cancel"
            :disabled="loading"
            @click="handleRefundClick"
          >
            {{ $t('failed_dialog.button_refund_now') }}
          </BaseButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import API from '@/services/api'
  import transactionService from '@/services/transactionService'
  import { TAKE_BATTERY_ERROR } from '@/constants.js'
  import { captureException } from '@/errors'
  import { sessionRecorder, UserEvent, SESSION_EVENTS } from '@/services/sessionRecorder'
  import BaseButton from '@/components/ui/BaseButton.vue'

  const TIMER_DURATION = 600 // 10 minutes in seconds

  export default {
    name: 'FailedDialog',
    components: {
      BaseButton
    },
    props: {
      transactionId: {
        type: [Number, String],
        required: true
      },
      cableType: {
        type: [Number, String],
        required: false,
        default: null
      },
      token: {
        type: String,
        required: false,
        default: null
      },
      paymentMethod: {
        type: [Number, String],
        required: false,
        default: null
      },
      transactionTimestamp: {
        type: [Number, String],
        required: false,
        default: null
      }
    },
    emits: ['try-again', 'refund', 'refund-success'],
    data() {
      return {
        tags: [],
        timeLeft: TIMER_DURATION,
        timer: null,
        loading: false,
        refundSuccess: false,
        isAutomaticRefund: false,
        showRefundConfirmation: false,
        errorMessage: null
      }
    },
    computed: {
      formattedTime() {
        const minutes = Math.floor(this.timeLeft / 60)
        const seconds = this.timeLeft % 60
        return `${minutes}:${seconds.toString().padStart(2, '0')}`
      },
      storageKey() {
        return `evaluation_timer_${this.transactionId}`
      }
    },
    mounted() {
      this.loadTags()
      this.startTimer()
    },
    beforeUnmount() {
      this.stopTimer()
    },
    methods: {
      // ============ Tags Management ============
      async loadTags() {
        try {
          const params = this.transactionId ? { transaction_id: this.transactionId } : {}
          const response = await API.get(`evaluation/${TAKE_BATTERY_ERROR}/tags`, { params })
          if (response?.data?.tags) {
            const lang = sessionStorage.getItem('lang')
            this.tags = response.data.tags.map(tag => ({
              id: tag.id,
              tag: lang === 'en' ? tag.tag_en : tag.tag,
              active: tag.selected === true,
              blinking: false
            }))
          }
        } catch (error) {
          console.error('Error loading tags:', error)
          captureException(error)
        }
      },

      onTagClick(tagId) {
        this.toggleTag(tagId)
      },

      toggleTag(tagId) {
        this.tags = this.tags.map(tag => ({
          ...tag,
          active: tag.id === tagId ? !tag.active : false
        }))
      },

      async submitTags() {
        const selectedTagIds = this.tags.filter(tag => tag.active).map(tag => tag.id)
        if (selectedTagIds.length === 0) return true

        try {
          await API.post('transaction/evaluation', {
            transaction_id: this.transactionId,
            tag_ids: selectedTagIds
          })
          return true
        } catch (error) {
          console.error('Error submitting tags:', error)
          captureException(error)
          return false
        }
      },

      // ============ Timer Management ============
      getNormalizedTimestamp(val) {
        if (!val) return null
        if (typeof val === 'number') {
          const MILLISECONDS_THRESHOLD = 10000000000
          return val < MILLISECONDS_THRESHOLD ? val * 1000 : val
        }
        const parsed = new Date(val).getTime()
        return isNaN(parsed) ? null : parsed
      },

      startTimer() {
        const now = Date.now()
        const baseTimestamp = this.getNormalizedTimestamp(this.transactionTimestamp)
        let expiry = null

        if (baseTimestamp) {
          expiry = baseTimestamp + TIMER_DURATION * 1000
          localStorage.setItem(this.storageKey, expiry.toString())
        } else {
          const storedExpiry = localStorage.getItem(this.storageKey)
          if (storedExpiry) {
            expiry = parseInt(storedExpiry, 10)
          } else {
            expiry = now + TIMER_DURATION * 1000
            localStorage.setItem(this.storageKey, expiry.toString())
          }
        }

        const remaining = Math.max(0, Math.floor((expiry - now) / 1000))
        this.timeLeft = remaining

        if (this.timeLeft <= 0) {
          this.performRefund(true)
          return
        }

        this.timer = setInterval(() => {
          this.updateTimer()
        }, 1000)
      },

      updateTimer() {
        const storedExpiry = localStorage.getItem(this.storageKey)
        if (!storedExpiry) return

        const remaining = Math.max(0, Math.floor((parseInt(storedExpiry, 10) - Date.now()) / 1000))
        this.timeLeft = remaining

        if (this.timeLeft <= 0) {
          this.stopTimer()
          this.performRefund(true)
        }
      },

      resumeTimer() {
        this.stopTimer()
        const now = Date.now()
        const baseTimestamp = this.getNormalizedTimestamp(this.transactionTimestamp)
        let expiry = null

        if (baseTimestamp) {
          expiry = baseTimestamp + TIMER_DURATION * 1000
        } else {
          const storedExpiry = localStorage.getItem(this.storageKey)
          expiry = storedExpiry ? parseInt(storedExpiry, 10) : now + TIMER_DURATION * 1000
        }

        const remaining = Math.max(0, Math.floor((expiry - now) / 1000))
        this.timeLeft = remaining

        if (this.timeLeft > 0) {
          this.timer = setInterval(() => {
            this.updateTimer()
          }, 1000)
        }
      },

      resetTimer() {
        this.stopTimer()
        const expiry = Date.now() + TIMER_DURATION * 1000
        localStorage.setItem(this.storageKey, expiry.toString())
        this.timeLeft = TIMER_DURATION
        this.timer = setInterval(() => {
          this.updateTimer()
        }, 1000)
      },

      stopTimer() {
        if (this.timer) {
          clearInterval(this.timer)
          this.timer = null
        }
      },

      clearTimerStorage() {
        localStorage.removeItem(this.storageKey)
        localStorage.removeItem(`evaluation_timestamp_${this.transactionId}`)
        localStorage.removeItem('evaluation_inprogress_transaction_id')
      },

      // ============ Action Handlers ============
      async handleTryAgain() {
        sessionRecorder.addUserEvent(new UserEvent(SESSION_EVENTS.TRY_AGAIN_INITIATED, this.transactionId))

        this.loading = true
        this.errorMessage = null
        this.stopTimer()

        try {
          await this.submitTags()
          const response = await transactionService.retryTransaction(
            this.transactionId,
            this.cableType,
            this.paymentMethod
          )
          if (response?.data?.success === false) {
            this.errorMessage = response.data.error || this.$t('failed_dialog.message_retry_failed')
            this.resumeTimer()
            return
          }

          this.clearTimerStorage()
          this.$emit('try-again', {
            transactionId: response.data?.transaction_id || this.transactionId,
            selectedSlot: response.data?.selected_slot,
            totalSlots: response.data?.terminal?.total_slots
          })
        } catch (error) {
          console.error('Retry failed:', error)
          captureException(error)
          this.errorMessage = error?.response?.data?.error || this.$t('failed_dialog.message_retry_failed')
          this.resumeTimer()
        } finally {
          this.loading = false
        }
      },

      handleRefundClick() {
        this.showRefundConfirmation = true
      },

      cancelRefund() {
        this.showRefundConfirmation = false
      },

      async performRefund(isAutomatic = false) {
        sessionRecorder.addUserEvent(new UserEvent(SESSION_EVENTS.REFUND_INITIATED, this.transactionId))
        this.loading = true
        this.errorMessage = null
        this.stopTimer()

        try {
          await this.submitTags()
          await transactionService.refundTransaction(this.transactionId)
          this.clearTimerStorage()
          this.refundSuccess = true
          this.isAutomaticRefund = isAutomatic
          this.showRefundConfirmation = false
          this.$emit('refund-success', {
            isAutomatic,
            transactionId: this.transactionId,
            paymentMethod: this.paymentMethod
          })
        } catch (error) {
          console.error('Refund failed:', error)
          captureException(error)
          const isTechnicalError =
            !error?.response &&
            (error?.message === 'Network Error' ||
              error?.message === 'Refund failed' ||
              error?.code === 'ERR_NETWORK' ||
              error?.code === 'ECONNABORTED')
          this.errorMessage =
            error?.response?.data?.error ||
            (!isTechnicalError && error?.message ? error.message : null) ||
            this.$t('failed_dialog.refund_fail')
          this.resumeTimer()
          this.showRefundConfirmation = false
        } finally {
          this.loading = false
        }
      },

      finishRefund() {
        this.$emit('refund')
        this.$emit('refund-success', {
          isAutomatic: this.isAutomaticRefund,
          transactionId: this.transactionId,
          paymentMethod: this.paymentMethod
        })
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .failed-dialog-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    z-index: 10000;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    overflow-y: auto;
    box-sizing: border-box;
  }

  .failed-dialog-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    padding: 18px 24px 28px;
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    max-height: 90dvh;
    overflow-y: auto;
    position: relative;
    z-index: 10001;
    animation: dialogPopIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .modal-header-actions {
    display: flex;
    justify-content: flex-end;
  }

  @keyframes dialogPopIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(12px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .dialog-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2c3e50;
    line-height: 1.3;
  }

  .section-subtitle {
    font-size: 0.95rem;
    color: #7f8c8d;
    font-weight: 500;
  }

  .dialog-description {
    font-size: 0.95rem;
    color: #5d6d7e;
    line-height: 1.5;
  }

  /* Status Icons */
  .status-icon-wrapper {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;

    &.success {
      background: rgba(40, 167, 69, 0.1);
      color: #28a745;
    }

    &.warning {
      background: rgba(255, 193, 7, 0.1);
      color: #ffc107;
    }

    .status-icon {
      font-size: 2.5rem;
    }
  }

  /* Tags Styling */
  .tags-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }

  .modern-tag {
    background: #f8f9fa;
    border: 1.5px solid #e9ecef;
    border-radius: 12px;
    color: #495057;
    padding: 8px 16px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    -webkit-transition: all 0.2s ease;
    white-space: nowrap;
    min-height: 44px;
    /* iOS minimum touch target */
    display: inline-flex;
    align-items: center;
    -webkit-tap-highlight-color: transparent;
    /* Remove iOS tap highlight */
    -webkit-user-select: none;
    user-select: none;

    /* Only apply hover on devices with hover capability */
    @media (hover: hover) {
      &:hover {
        background: #e9ecef;
        border-color: #dee2e6;
      }
    }

    /* Active state for touch devices */
    &:active {
      background: #e9ecef;
      transform: scale(0.98);
    }

    &.active {
      background: main.$color-energy-blue;
      border-color: main.$color-energy-blue;
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(1, 106, 179, 0.25);
      -webkit-box-shadow: 0 4px 12px rgba(1, 106, 179, 0.25);
    }

    &.blinking {
      animation: tag-pulse 0.5s ease-in-out;
      -webkit-animation: tag-pulse 0.5s ease-in-out;
    }
  }

  @keyframes tag-pulse {
    0% {
      transform: scale(1);
    }

    50% {
      transform: scale(1.05);
    }

    100% {
      transform: scale(1);
    }
  }

  @-webkit-keyframes tag-pulse {
    0% {
      -webkit-transform: scale(1);
      transform: scale(1);
    }

    50% {
      -webkit-transform: scale(1.05);
      transform: scale(1.05);
    }

    100% {
      -webkit-transform: scale(1);
      transform: scale(1);
    }
  }

  .separator {
    border: 0;
    height: 1px;
    background-image: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0));
    opacity: 0.6;
  }

  /* Timer Styling */
  .timer-card {
    background: #fdfefe;
    border: 1px solid #ebedef;
    border-radius: 16px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .timer-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .timer-icon {
    font-size: 1.5rem;
    color: main.$color-energy-blue;
    opacity: 0.8;
  }

  .timer-text {
    display: flex;
    flex-direction: column;
  }

  .timer-label {
    font-size: 0.85rem;
    font-weight: 700;
    color: #34495e;
  }

  .timer-sublabel {
    font-size: 0.75rem;
    color: #95a5a6;
  }

  .timer-value {
    font-size: 1.25rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: #2c3e50;

    &.timer-low {
      color: #e74c3c;
      animation: blinker 1s linear infinite;
      -webkit-animation: blinker 1s linear infinite;
    }
  }

  @keyframes blinker {
    50% {
      opacity: 0.5;
    }
  }

  @-webkit-keyframes blinker {
    50% {
      opacity: 0.5;
    }
  }

  /* Error Alert */
  .error-alert {
    background: #fff5f5;
    border-left: 4px solid #fc8181;
    color: #c53030;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .btn-cancel {
    background: transparent;
    color: #7f8c8d;

    &:hover {
      background: #f8f9fa;
      color: #34495e;
    }
  }
</style>
